// Disable rule as the component is external

/* eslint-disable react/jsx-pascal-case */
import classnames from 'classnames'
import * as React from 'react'
import * as Reakit from 'reakit/Combobox'
import { Portal } from 'reakit/Portal'

import { Box, BoxProps } from '../box'
import { getExtendedDialogState, useZIndex } from '../dialog'
import { Input, InputProps } from '../input'
import { ComboBoxItem } from './combo-box-item'

interface ComboBoxProps extends Omit<BoxProps, 'onChange'> {
  /**
   * The returned object from `useComboBoxState`
   */
  state: Reakit.unstable_ComboboxStateReturn

  /**
   * Additional props for the input
   */
  inputProps?: Partial<InputProps> & Omit<Partial<Reakit.unstable_ComboboxProps>, 'size'>

  /**
   * Additional props for the popover
   */
  popoverProps?: Partial<Reakit.unstable_ComboboxPopoverProps>

  /**
   * Callback when user presses enter
   */
  onChange: (value: string) => void

  /**
   * Id for testing purposes
   */
  inputTestId?: string
}

export function ComboBox({
  state,
  children,
  inputProps,
  popoverProps,
  onChange,
  inputTestId,
  'aria-label': ariaLabel,
  ...props
}: React.PropsWithChildren<ComboBoxProps>) {
  const { isFullyClosed } = getExtendedDialogState(state)
  const zIndex = useZIndex()

  return (
    <>
      <Reakit.unstable_Combobox
        {...state}
        {...inputProps}
        // We need to set loop to false otherwise we get TS error from `Input` component
        loop={undefined}
        as={Input}
        aria-label={ariaLabel}
        onKeyDown={(ev: React.KeyboardEvent) => {
          if (ev.key === 'Enter') {
            onChange(state.inputValue)
          }
          inputProps?.onKeyDown?.(ev)
        }}
        data-testid={inputTestId}
      />
      {!isFullyClosed && (
        <Portal>
          <Reakit.unstable_ComboboxPopover {...state} {...popoverProps} aria-label={ariaLabel}>
            {({ onTransitionEnd, onAnimationEnd, ...innerProps }) => (
              <div {...innerProps} className={classnames(innerProps.className, zIndex, 'finviz-popover outline-none')}>
                <Box
                  {...props}
                  onTransitionEnd={onTransitionEnd}
                  onAnimationEnd={onAnimationEnd}
                  data-placement={state.placement}
                  className={classnames(props.className, 'flex flex-col items-stretch space-y-px p-2')}
                >
                  {React.Children.map(children, (child) => {
                    if (!React.isValidElement(child)) return child

                    if (child.type === ComboBoxItem) {
                      return React.cloneElement(
                        child as React.ReactElement<React.ComponentProps<typeof ComboBoxItem>>,
                        {
                          state,
                          onMouseUp: () => {
                            onChange(child.props.value)
                          },
                        }
                      )
                    }

                    return child
                  })}
                </Box>
              </div>
            )}
          </Reakit.unstable_ComboboxPopover>
        </Portal>
      )}
    </>
  )
}
